import React, { Fragment } from 'react';
import { common, query } from '@kurtosys/ksys-app-template';
import { GlobalDisclaimerLevel, ITargettedDisclaimers } from '@kurtosys/types/disclaimers/index.js';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { QueryStore } from '../../../shared/QueryStore.js';
import { IDisclaimerProps } from '../../overview/Disclaimer/models/index.js';
import Disclaimers from '../../overview/Disclaimers/Disclaimers.js';
import { IDisclaimersProps } from '../../overview/Disclaimers/models/index.js';
import Heading from '../Heading/Heading.js';
import Superscript from '../Table/styledComponents/Superscript.js';
import Tooltip from '../Tooltip/Tooltip.js';
import Translate from '../Translate/Translate.js';

import { ICardConfiguration, ICardProps, ICardState, ICardTooltip } from './models/index.js';
import Body from './styledComponents/Body.js';
import Footer from './styledComponents/Footer.js';
import Header from './styledComponents/Header.js';
import Subtitle from './styledComponents/Subtitle.js';
import Title from './styledComponents/Title.js';
import Wrapper from './styledComponents/Wrapper.js';
import { CARD_CATEGORY_KEY, CARD_COMPONENT_KEY } from './Card.meta.js';

export class Card extends ComponentBase<ICardProps, ICardState, ICardConfiguration> {
	categoryKey = CARD_CATEGORY_KEY;
	componentKey = CARD_COMPONENT_KEY;

	constructor(props: ICardProps) {
		super(props);

		makeObservable(this);
	}

	getTooltipTarget(target: ICardTooltip['target']) {
		const { tooltips } = this.props;
		let response;
		if (Array.isArray(tooltips) && tooltips.length > 0) {
			response = tooltips.find((tooltip) => tooltip.target === target);
		}
		return response;
	}

	getTooltip(tooltip: ICardTooltip) {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { target, ...tooltipProps } = tooltip;
		return tooltipProps && <Tooltip {...tooltipProps} />;
	}

	static generateSuperscriptKey(appKey: string, disclaimerKey: string | undefined) {
		return disclaimerKey ? `${appKey}-${disclaimerKey}` : appKey;
	}

	static addSuperscriptDisclaimers(
		appKey: string,
		superscripts: query.disclaimers.GlobalDisclaimerStoreValue[],
		targetedDisclaimers: ITargettedDisclaimers,
		conditionalContext: any,
		responseContext: any,
		globalDisclaimerStore: query.stores.GlobalDisclaimerStore,
		queryStore: QueryStore,
	) {
		const { disclaimers, conditional } = targetedDisclaimers;
		if (conditional) {
			const conditionalHelper = new common.helpers.ConditionalHelper({ queryClass: query.Query }, conditional);
			if (
				conditionalHelper.matchesWithOptions({
					instance: conditionalContext,
					executionOptions: { responseContext: conditionalContext },
				})
			) {
				superscripts.push(...globalDisclaimerStore.add(appKey, responseContext, queryStore, ...disclaimers));
			}
		} else {
			superscripts.push(...globalDisclaimerStore.add(appKey, responseContext, queryStore, ...disclaimers));
		}
	}

	static loadSuperscripts = (
		appKey: string,
		queryStore: QueryStore,
		cardProps: ICardProps,
		disclaimerKey?: string,
	) => {
		const { disclaimers } = cardProps;
		const superscriptKey = Card.generateSuperscriptKey(appKey, disclaimerKey);
		if (disclaimers) {
			const { title: rawTitleDisclaimers, subTitle: rawSubTitleDisclaimers } = disclaimers;

			query.queryUtils.globalDisclaimers.initializeGlobalDisclaimers();
			const globalDisclaimerStore =
				query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore() as query.stores.GlobalDisclaimerStore;

			const titleDisclaimers: query.disclaimers.GlobalDisclaimerStoreValue[] = [];
			const subTitleDisclaimers: query.disclaimers.GlobalDisclaimerStoreValue[] = [];

			if (rawTitleDisclaimers) {
				for (const targetedTitleDisclaimer of rawTitleDisclaimers) {
					Card.addSuperscriptDisclaimers(
						superscriptKey,
						titleDisclaimers,
						targetedTitleDisclaimer,
						cardProps,
						cardProps,
						globalDisclaimerStore,
						queryStore,
					);
				}

				if (titleDisclaimers.length > 0) {
					disclaimers._titleSuperscripts = titleDisclaimers
						.map((titleDisclaimer) => titleDisclaimer.superscript)
						.sort();
				}
			}
			if (rawSubTitleDisclaimers) {
				for (const targetedSubTitleDisclaimer of rawSubTitleDisclaimers) {
					Card.addSuperscriptDisclaimers(
						superscriptKey,
						subTitleDisclaimers,
						targetedSubTitleDisclaimer,
						cardProps,
						cardProps,
						globalDisclaimerStore,
						queryStore,
					);
				}
				if (subTitleDisclaimers.length > 0) {
					disclaimers._subTitleSuperscripts = subTitleDisclaimers
						.map((subTitleDisclaimer) => subTitleDisclaimer.superscript)
						.sort();
				}
			}
		}
	};

	@computed
	get header() {
		const { titleId, title, titleLevel, subtitle, subtitleLevel, disclaimers } = this.props;
		const titleTooltip = this.getTooltipTarget('title');
		const subTitleTooltip = this.getTooltipTarget('subTitle');
		const titleSuperscripts = disclaimers && disclaimers._titleSuperscripts;
		const subTitleSuperscripts = disclaimers && disclaimers._subTitleSuperscripts;

		return (
			<Header>
				{title && !titleLevel && (
					<Title id={titleId || `heading-h2-${common.commonUtils.generateGuid()}`}>
						{/* Adding title id above to make sure we are backwards compatible, otherwise
						 * the id will only render when titleLevel is passed.
						 **/}
						<Translate>{title}</Translate>
						{titleTooltip && this.getTooltip(titleTooltip)}
						{titleSuperscripts && <Superscript>{titleSuperscripts.join(',')}</Superscript>}
					</Title>
				)}
				{title && titleLevel && (
					<Fragment>
						<Heading id={titleId} level={titleLevel} title={title} />
						{titleTooltip && this.getTooltip(titleTooltip)}
						{titleSuperscripts && <Superscript>{titleSuperscripts.join(',')}</Superscript>}
					</Fragment>
				)}
				{subtitle && !subtitleLevel && (
					<Subtitle>
						<Translate>{subtitle}</Translate>
						{subTitleTooltip && this.getTooltip(subTitleTooltip)}
						{subTitleSuperscripts && <Superscript>{subTitleSuperscripts.join(',')}</Superscript>}
					</Subtitle>
				)}
				{subtitle && subtitleLevel && (
					<Fragment>
						<Heading level={subtitleLevel} title={subtitle} />
						{subTitleTooltip && this.getTooltip(subTitleTooltip)}
						{subTitleSuperscripts && <Superscript>{subTitleSuperscripts.join(',')}</Superscript>}
					</Fragment>
				)}
			</Header>
		);
	}

	getDisclaimersProps = () => {
		const { footerDisclaimers, disclaimerKey } = this.props;
		const globalDisclaimersStore = query.queryUtils.globalDisclaimers.getGlobalDisclaimerStore();
		const response: IDisclaimersProps = JSON.parse(common.commonUtils.stringifySafe(footerDisclaimers || {}));
		if (globalDisclaimersStore) {
			const appKey = (this.props.configuration && this.props.configuration.appKey) || '123';
			const superscriptKey = Card.generateSuperscriptKey(appKey, disclaimerKey);

			if (superscriptKey) {
				const globalDisclaimersForApp = globalDisclaimersStore.get(GlobalDisclaimerLevel.app, superscriptKey);
				if (globalDisclaimersForApp && globalDisclaimersForApp.length > 0) {
					if (!response.disclaimers) {
						response.disclaimers = [];
					}
					response.disclaimers.push(
						...globalDisclaimersForApp.map((globalDisclaimerForApp) => {
							const disclaimerProps: IDisclaimerProps = {
								text: globalDisclaimerForApp.text,
								superscript:
									typeof globalDisclaimerForApp.superscript === 'number'
										? globalDisclaimerForApp.superscript.toString()
										: globalDisclaimerForApp.superscript,
							};
							return disclaimerProps;
						}),
					);
				}
			}
		}
		return response;
	};

	@computed
	get footer() {
		const { footerContent, hideDisclaimers } = this.props;
		const disclaimersProps = this.getDisclaimersProps();

		if (footerContent || disclaimersProps) {
			return (
				<Footer>
					{footerContent && <Translate>{footerContent}</Translate>}
					{!hideDisclaimers && disclaimersProps && <Disclaimers {...disclaimersProps} />}
				</Footer>
			);
		}
	}

	render() {
		const { children, className } = this.props;
		return (
			<Wrapper className={className}>
				{this.header}
				<Body>
					<Translate>{children}</Translate>
				</Body>
				{this.footer}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Card), CARD_CATEGORY_KEY, CARD_COMPONENT_KEY);
